import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, Smartphone, ArrowRight, Search, Settings } from "lucide-react";
import { restaurants } from "../data/restaurants";
import { useNavigate } from "react-router-dom";
import TestPanel from "../components/TestPanel.jsx";

const QRCodePage = () => {
  const navigate = useNavigate();
  const [selectedRestaurant, setSelectedRestaurant] = useState(restaurants[0]);
  const [showQR, setShowQR] = useState(false);

  const generateQRUrl = (restaurantId) => {
    return `${window.location.origin}/menu/${restaurantId}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-4">
            <QrCode className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Restaurant QR Menu
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Scan the QR code to view our digital menu and place your order
            directly from your phone
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Restaurant Selection */}
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Select Restaurant
            </h2>

            <div className="space-y-4">
              {restaurants.map((restaurant) => (
                <div
                  key={restaurant.id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    selectedRestaurant.id === restaurant.id
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setSelectedRestaurant(restaurant)}
                >
                  <div className="flex items-center space-x-4">
                    <img
                      src={restaurant.logo}
                      alt={restaurant.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">
                        {restaurant.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {restaurant.description}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {restaurant.address}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <button
                onClick={() => setShowQR(true)}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                <QrCode className="w-5 h-5" />
                <span>Generate QR Code</span>
              </button>

              <button
                onClick={() => navigate("/order-status")}
                className="w-full btn-secondary flex items-center justify-center space-x-2"
              >
                <Search className="w-5 h-5" />
                <span>Check Order Status</span>
              </button>

              <button
                onClick={() => navigate("/admin")}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors"
              >
                <Settings className="w-5 h-5" />
                <span>Admin Panel</span>
              </button>
            </div>
          </div>

          {/* QR Code Display */}
          <div className="flex flex-col items-center">
            {showQR ? (
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {selectedRestaurant.name}
                  </h3>
                  <p className="text-gray-600">Scan to view menu</p>
                </div>

                <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                  <QRCodeSVG
                    value={generateQRUrl(selectedRestaurant.id)}
                    size={256}
                    level="M"
                    includeMargin={true}
                  />
                </div>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-500 mb-4">
                    Or visit directly:
                  </p>
                  <a
                    href={generateQRUrl(selectedRestaurant.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Open Menu
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="w-64 h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Smartphone className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">QR Code will appear here</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-12 bg-white rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            How it works
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-primary-600 font-bold">1</span>
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Scan QR Code</h4>
              <p className="text-sm text-gray-600">
                Use your phone's camera to scan the QR code on the table
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-primary-600 font-bold">2</span>
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Browse Menu</h4>
              <p className="text-sm text-gray-600">
                View our digital menu with photos and descriptions
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-primary-600 font-bold">3</span>
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Place Order</h4>
              <p className="text-sm text-gray-600">
                Add items to cart and place your order directly
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Test Panel for Development */}
      <TestPanel />
    </div>
  );
};

export default QRCodePage;
